import HiddenInput from "@/components/form/components/HiddenInput";
import { InputNumber } from "@/components/InputNumber";
import { selectStandardProps, selectTagsStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Col, Form, Modal, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateCommissionsLevelRule, useUpdateCommissionsLevelRule } from "../../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import type { UUID } from "../../../../../typings/global";
import { useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import InstitutionSelect from "../../../../enumerations/components/form/InstitutionSelect";
import ProductGroupSelect from "../../../../enumerations/components/form/ProductGroupSelect";
import ProductSelect from "../../../../enumerations/components/form/ProductSelect";
import { ProductFinancialSector } from "../../../../product/enums";
import { PRODUCT_SECTOR_TO_INSTITUTION_TYPE_MAP } from "../../../../product/utils";
import { CommissionSourceTag } from "../../../settings/components/CommissionSourceTag";
import { CommissionSource, commissionSourceTMap } from "../../../settings/enums";
import { CommissionsSettingsLevelRule, CreateUpdateCommissionsSettingsLevelRule } from "../../types";
import { convertCommissionsSettingsLevelRuleToCreateUpdateObject } from "../../utils";

const colSpan = 8;

interface Props {
  open: boolean;
  rule?: CommissionsSettingsLevelRule;
  levelId: UUID;
  onFormCancel: () => void;
}

export const CommissionsLevelRuleForm = ({ open, rule, levelId, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const isCurrentAgentTypePlus = useIsCurrentAgentTypePlus();

  const [form] = Form.useForm<CreateUpdateCommissionsSettingsLevelRule>();
  const sector = Form.useWatch("sector", form);
  const productGroupId = Form.useWatch("productGroupId", form);

  const createCommissionsLevelRule = useCreateCommissionsLevelRule();
  const updateCommissionsLevelRule = useUpdateCommissionsLevelRule();

  useEffect(() => {
    if (!open) {
      return;
    }

    if (rule) {
      form.setFieldsValue(convertCommissionsSettingsLevelRuleToCreateUpdateObject(rule));
    } else if (isCurrentAgentTypePlus) {
      form.setFieldsValue({ sector: ProductFinancialSector.ALL });
    }
  }, [rule, open, form]);

  const handleSectorChange = () => {
    form.setFieldsValue({
      productGroupId: undefined,
      institutionId: undefined,
      productId: undefined,
    });
  };

  const handleProductGroupIdChange = () => {
    form.setFieldsValue({ productId: undefined });
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (rule) {
          updateCommissionsLevelRule.mutate(
            { id1: levelId, id2: rule.id, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "commissions.level.attrs.rule", error.violations),
            },
          );
        } else {
          createCommissionsLevelRule.mutate(
            { id: levelId, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "commissions.level.attrs.rule", error.violations),
            },
          );
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.LARGE}
      open={open}
      title={rule ? t("commissions.level.titles.updateRule") : t("commissions.level.titles.createRule")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createCommissionsLevelRule.isPending || updateCommissionsLevelRule.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="commissionsSettingsLevelRuleForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item name="sector" label={t("product.enums.financialSector._label")} rules={[validations.notNull]}>
              <Select
                {...selectStandardProps}
                options={Object.keys(ProductFinancialSector).map((sector) => ({
                  value: sector,
                  label: t("product.enums.financialSector." + sector),
                }))}
                onChange={handleSectorChange}
              />
            </Form.Item>
          </Col>
        </Row>

        {sector !== ProductFinancialSector.ALL && (
          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <ProductGroupSelect
                formItemProps={{
                  name: "productGroupId",
                  label: t("commissions.level.attrs.rule.productGroupId"),
                  rules: [validations.none],
                }}
                selectProps={{
                  allowClear: true,
                  placeholder: !sector ? t("commissions.level.helpers.rule.productGroupPlaceholder") : null,
                  onChange: handleProductGroupIdChange,
                }}
                optionsProps={{ filterSectors: [sector] }}
              />
            </Col>

            <Col span={colSpan}>
              <InstitutionSelect
                formItemProps={{
                  name: "institutionId",
                  label: t("commissions.level.attrs.rule.institutionId"),
                  rules: [validations.none],
                }}
                selectProps={{
                  allowClear: true,
                  placeholder: !productGroupId ? t("commissions.level.helpers.rule.institutionPlaceholder") : null,
                }}
                optionsProps={{
                  hideAll: !productGroupId,
                  selected: rule?.institution ? [rule.institution] : undefined,
                  filterType: PRODUCT_SECTOR_TO_INSTITUTION_TYPE_MAP.get(sector),
                }}
              />
            </Col>

            <Col span={colSpan}>
              <ProductSelect
                formItemProps={{
                  name: "productId",
                  label: t("commissions.level.attrs.rule.productId"),
                  rules: [validations.none],
                }}
                selectProps={{
                  allowClear: true,
                  placeholder: !productGroupId ? t("commissions.level.helpers.rule.productPlaceholder") : null,
                }}
                optionsProps={{
                  productGroupId,
                  filterSectors: [sector],
                  groupByProductGroup: true,
                  hideAll: !productGroupId,
                }}
              />
            </Col>
          </Row>
        )}

        {isCurrentAgentTypePlus ? (
          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name="commissionAmountPerPoint"
                label={t("commissions.level.attrs.rule.commissionAmountPerPoint")}
                rules={[validations.notNull, validations.minNumber(0)]}
              >
                <InputNumber type="decimal" addon="euro" />
              </Form.Item>
            </Col>
          </Row>
        ) : (
          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name="source"
                label={t("commissions.settings.enums.commissionSource._label")}
                rules={[validations.notNull]}
              >
                <Select
                  {...selectTagsStandardProps(commissionSourceTMap)}
                  options={Object.values(CommissionSource).map((source) => ({
                    value: source,
                    label: <CommissionSourceTag source={source} />,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name="initialCommissionRate"
                label={t("commissions.level.attrs.rule.initialCommissionRate")}
                rules={[validations.notNull, validations.minNumber(0)]}
              >
                <InputNumber type="decimal" addon="percentage" />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name="subsequentCommissionRate"
                label={t("commissions.level.attrs.rule.subsequentCommissionRate")}
                rules={[validations.notNull, validations.minNumber(0)]}
              >
                <InputNumber type="decimal" addon="percentage" />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Form>
    </Modal>
  );
};
